// Status tones and labels shared by the status marks (status.tsx) and the pages.
import { messages } from "../i18n";

export type Tone = "green" | "orange" | "red" | "blue" | "gray" | "primary" | "secondary";

/**
 * Tones for the platform's state machines (docs/03): green a success or a steady state, blue in
 * progress, orange waiting on a human, red a failure, gray stopped, cancelled or gone. The API
 * carries statuses as plain strings, so an unknown one is gray rather than an error.
 */
const RUN: Record<string, Tone> = {
  queued: "gray",
  admitted: "blue",
  starting: "blue",
  running: "blue",
  waiting_for_human: "orange",
  collecting: "blue",
  succeeded: "green",
  failed: "red",
  timed_out: "red",
  skipped: "gray",
  cancelled: "gray",
};

const ENVIRONMENT: Record<string, Tone> = {
  requested: "blue",
  provisioning: "blue",
  configuring_network: "blue",
  ready: "green",
  stopped: "gray",
  failed: "red",
  deleting: "gray",
  deleted: "gray",
};

const ORGANISATION: Record<string, Tone> = { active: "green", suspended: "orange", deleting: "gray" };

export const runTone = (status: string): Tone => RUN[status] ?? "gray";
export const environmentTone = (status: string): Tone => ENVIRONMENT[status] ?? "gray";
export const organisationTone = (status: string): Tone => ORGANISATION[status] ?? "gray";

/** A run is live while a worker may be doing something for it. */
export const ACTIVE_RUN = new Set(["admitted", "starting", "running", "collecting", "waiting_for_human"]);

/** "waiting_for_human" → "waiting for human", in the active language; an unknown status as sent. */
export function statusLabel(status: string): string {
  const known: Partial<Record<string, string>> = messages().common.status;
  return known[status] ?? status.replaceAll("_", " ");
}
